import * as React from 'react';
import Overlay from '../../../components/Overlay/Overlay';
import Menu from '../../../components/Menu/Menu';
import ConfirmPopup from '../../../components/ConfirmPopup/ConfirmPopup';
import { connect, ConnectedProps } from 'react-redux';
import AlanButtonPopupSettingsForm from '../AlanButtonPopupSettingsForm/AlanButtonPopupSettingsForm';
import scriptDraftsService from '../../../../services/ScriptDraftService';
import { IScript } from '../../../../models/IScript';
import { IStore } from '../../../../redux/initialState';
import { IAlanBtnPopup, IAlanBtnPopupPropPosition } from '../../../../models/IAlanBtnPopup';
import { AlanButtonPopupEditorModes } from './alanButtonPopupEditorModes';
import { DeviceMode } from '../AlanButtonPopupSettingsForm/deviceMode';
import { useCallback, useEffect, useState } from 'react';
import { IProject } from '../../../../models/IProject';
import * as scriptEditorActions from '../../../../redux/scriptEditor/actions.scriptEditor';
import { IHighlightLineParams } from '../../../../redux/scriptEditor/actions.scriptEditor';
import { ContextParser } from '../../../../classes/ContextParser';
import { confirmAlert } from '../../../../services/mini/alanConfirmAlert';
import { cleanupParams } from '../../../../services/mini/cleanupParams';
import { Environment } from '../../../../const/environment';

function parseCode(codeStr) {
    const parser = new ContextParser();
    parser.parseData(codeStr, 'test');
    return parser;
}

function hasContentTheSame(initProp, propInView) {
    return prepareContentToShow(initProp) === propInView;
}

function prepareContentToShow(content: string): string {
    return content.replace(/\\n/gi, '\n');
}

type AlanButtonPopupsEditorProps = PropsFromRedux & {
    project: IProject
    environment: Environment
    selectedKey: string
    script: IScript
    onClose: () => void
    onSave: (scriptText: string) => void
};
function AlanButtonPopupsEditor({
    project,
    environment,
    alanBtnPopups,
    selectedKey,
    script,
    highlightLine,
    onClose,
    onSave,
}: AlanButtonPopupsEditorProps) {
    const [updateTs, setUpdateTs] = useState(null);
    const [previewMode, setPreviewMode] = useState(AlanButtonPopupEditorModes.Preview);
    const [selectedPopupKey, setSelectedPopupKey] = useState(selectedKey);
    const [popupsData, setPopupsData] = useState({});
    const [deviceMode, setDeviceMode] = useState(DeviceMode.Desktop);
    const [scriptText, setScriptText] = useState(null);

    useEffect(() => {
        async function fillScriptText() {
            const scriptText = await scriptDraftsService.getScriptText(script);
            setScriptText(scriptText);
        }
        fillScriptText();
    }, [script]);

    useEffect(() => {
        const parsedPopupsData = {} as IAlanBtnPopup;
        Object.keys(alanBtnPopups).forEach((key) => {
            parsedPopupsData[key] = { ...alanBtnPopups[key] };
            parsedPopupsData[key].html = prepareContentToShow(parsedPopupsData[key].html);
            parsedPopupsData[key].style = prepareContentToShow(parsedPopupsData[key].style);
        });
        setPopupsData(parsedPopupsData);
    }, [alanBtnPopups]);

    const updateDataCb = useCallback((propName, value) => {
        const curPopupsData = { ...popupsData };
        curPopupsData[selectedPopupKey][propName] = value;
        setPopupsData(curPopupsData);
    }, [popupsData, selectedPopupKey]);

    const discardChangesCb = useCallback((key: string) => {
        const curPopupsData = { ...popupsData };
        curPopupsData[key].html = prepareContentToShow(alanBtnPopups[key].html);
        curPopupsData[key].style = prepareContentToShow(alanBtnPopups[key].style);
        setPopupsData(curPopupsData);
        setUpdateTs(Date.now());
    }, [alanBtnPopups, popupsData]);

    const goToCb = useCallback((key: string) => {
        onClose();
        highlightLine({
            scriptName: script.name,
            lineNumber: alanBtnPopups[key].togglerPosition.line,
            character: 0,
            project,
        });
    }, [alanBtnPopups, highlightLine, onClose, project, script.name]);

    const popupKeys = Object.keys(popupsData);
    const changedPopups = {};

    for (let i = 0; i < popupKeys.length; i++) {
        const curKey = popupKeys[i];
        if (!hasContentTheSame(alanBtnPopups[curKey].html, popupsData[curKey].html) ||
            !hasContentTheSame(alanBtnPopups[curKey].style, popupsData[curKey].style) ||
            (alanBtnPopups[curKey].params && (popupsData[curKey].params !== (cleanupParams(alanBtnPopups[curKey].params) === '' ? '{}' : alanBtnPopups[curKey].params)))
        ) {
            changedPopups[curKey] = true;
        }
    }

    const saveChangesCb = useCallback(() => {
        onClose();
        let resultScript = scriptText;
        let updatedAlanBtnPopups = { ...alanBtnPopups };
        let keyIndex = 0;

        Object.keys(alanBtnPopups).forEach(() => {
            const initPopupKey = Object.keys(updatedAlanBtnPopups)[keyIndex];
            const popupsDataKey = Object.keys(popupsData)[keyIndex];
            if (changedPopups[initPopupKey]) {
                let initPopup = updatedAlanBtnPopups[initPopupKey] as IAlanBtnPopup;

                resultScript = fixQuotesIfNeeded(resultScript, popupsData[popupsDataKey].html, initPopup.htmlPosition);
                resultScript = updateScriptByProps(resultScript, initPopup.htmlPosition, popupsData[popupsDataKey].html.replaceAll('\n', '\\n').replaceAll('\'', '\\\''));
                updatedAlanBtnPopups = parseCode(resultScript).alanBtnPopups;


                initPopup = updatedAlanBtnPopups[initPopupKey] as IAlanBtnPopup;
                resultScript = fixQuotesIfNeeded(resultScript, popupsData[popupsDataKey].style, initPopup.stylePosition);
                resultScript = updateScriptByProps(resultScript, initPopup.stylePosition, popupsData[popupsDataKey].style.replaceAll('\n', '\\n').replaceAll('\'', '\\\''));
                updatedAlanBtnPopups = parseCode(resultScript).alanBtnPopups;
                initPopup = updatedAlanBtnPopups[initPopupKey] as IAlanBtnPopup;

                if (initPopup.paramsPosition) {
                    resultScript = updateScriptByProps(resultScript, initPopup.paramsPosition, popupsData[popupsDataKey].params);
                }
                updatedAlanBtnPopups = parseCode(resultScript).alanBtnPopups;
            }
            keyIndex++;
        });
        onSave(resultScript);
    }, [alanBtnPopups, changedPopups, onClose, onSave, popupsData, scriptText]);

    function updateScriptByProps(script: string, propData: IAlanBtnPopupPropPosition, textToAdd: string) {
        return script.slice(0, propData.startChar) + textToAdd + script.slice(propData.endChar, script.length);
    }

    function fixQuotesIfNeeded(script, text, position) {
        if (script[position.startChar - 1] === '"' &&
            text.indexOf('"') > -1 &&
            text.indexOf('`') === -1
        ) {
            return script.substring(0, position.startChar - 1) + '`' + script.substring(position.startChar, position.endChar) + '`' + script.substring(position.endChar + 1, script.length);
        }
        return script;
    }

    const isSaveBtnActive = Object.keys(changedPopups).length > 0;

    const closeCb = useCallback(() => {
        if (isSaveBtnActive) {
            confirmAlert({
                customUI: ({ onClose: onCloseConfirmAlert }) => {
                    return (
                        <ConfirmPopup
                            onClose={() => {
                                onCloseConfirmAlert();
                            }}
                            okBtnLabel="Close"
                            cancelBtnLabel="Cancel"
                            okBtnCb={() => {
                                onCloseConfirmAlert();
                                onClose();
                            }}
                            title="Warning"
                            message={<div>You have unsaved changes that will be lost after closing. Are you sure you want to close Alan Button Popup Editor?</div>}
                        />
                    );
                }
            });
        } else {
            onClose();
        }
    }, [isSaveBtnActive, onClose]);

    return <Overlay>
        <div className="alan-button-popups-editor">
            <div className={`alan-button-popups-editor__list ${previewMode === AlanButtonPopupEditorModes.FullScreen ? 'collapsed' : ''}`} >
                <div className="alan-button-popups-editor__list-header" onClick={closeCb}><i className="icon icon-alan-chevron-fat" />Back to script</div>
                <div className="alan-button-popups-editor__list-body" >
                    {popupKeys.map((key, index) => {
                        return <div
                            key={key}
                            className={`alan-button-popups-editor__list-item ${key === selectedPopupKey && 'selected'} ${changedPopups[key] && 'was-changed'}`}
                            onClick={() => setSelectedPopupKey(key)}>
                            <div className="alan-button-popups-editor__list-item-content">
                                {popupsData[key].name || `Line: ${alanBtnPopups[key].togglerPosition.line}`}
                            </div>
                            <Menu
                                fixed={true}
                                width={162}
                                top={undefined}>
                                <div onClick={() => { goToCb(key); }}>
                                    <i className="icon icon-arrow-right" />Go to popup
                                </div>
                                <div onClick={() => discardChangesCb(key)} className={!changedPopups[key] ? 'disabled' : ''}>
                                    <i className="icon icon-alan-arrow-round-right-left-fat" />Discard changes
                                </div>
                            </Menu>
                        </div>;
                    })}
                </div>
                <div className="alan-button-popups-editor__list-footer" >
                    <div
                        className={`alan-button-popups-editor__save-btn action-btn middle width-100p ${!isSaveBtnActive && 'disabled'}`}
                        onClick={saveChangesCb}>
                        Save Changes
                    </div>
                </div>
            </div>
            <div className="alan-button-popups-editor__editor" >
                {(selectedPopupKey && popupsData[selectedPopupKey]) &&
                    <AlanButtonPopupSettingsForm
                        key={`${selectedPopupKey}-${updateTs}-${previewMode}`}
                        html={popupsData[selectedPopupKey].html}
                        style={popupsData[selectedPopupKey].style}
                        params={popupsData[selectedPopupKey].params}
                        type={popupsData[selectedPopupKey].type}
                        paramsCanBeCompiledInPreview={popupsData[selectedPopupKey].paramsCanBeCompiledInPreview}
                        project={project}
                        environment={environment}
                        previewMode={previewMode}
                        deviceMode={deviceMode}
                        buttonMarginInPopup={popupsData[selectedPopupKey].buttonMarginInPopup}
                        setDeviceMode={setDeviceMode}
                        onDataUpdated={updateDataCb}
                        openFullScreenMode={() => { setPreviewMode(AlanButtonPopupEditorModes.FullScreen); }}
                        closeFullScreenMode={() => { setPreviewMode(AlanButtonPopupEditorModes.Preview); }}
                        onSave={saveChangesCb}
                    />}
            </div>
        </div>
    </Overlay>;
}
function mapStateToProps({ codeMetaData }: IStore) {
    return {
        alanBtnPopups: codeMetaData.alanBtnPopups,
    };
}


function mapDispatchToProps(dispatch) {
    return {
        highlightLine: (param: IHighlightLineParams) => dispatch(scriptEditorActions.highlightLine(param)),
    };

}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AlanButtonPopupsEditor);
